import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { START_DATE } from 'react-dates/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome/index';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/index';
import Header from '../components/Header';
import parameters from '../../../config/parameters';
import configs from '../../../config/config';
import OptionRow from '../../Profile/OptionRow';
import { changeMemberRoleRequest } from '../../../store/actionCreators/club';
import {openDialog} from "../../../store/actionCreators/dialog";
import Avatar from "../../Units/Avatar";
import RealTabSwitcher from '../../Units/RealTabSwitcher';
import Calendar from '../../Units/Calendar';
import { MemberStats } from './components';
import { memberDetailsRequest } from '../../../store/actionCreators/member';
import {
  getAccountAvatars,
  getCurrentClubApprovedMembers,
  getRoleInCurrentClub
} from '../../../store/selectors';

// const roles =['manager', 'agent', 'player'];
const roles = configs.userExtraRoles ? [...configs.userExtraRoles, 'player'] : ['player'];
const MemberDetails = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const {id: clubId} = useParams();
    const { memberId } = useParams();
    const calendar = Calendar.useCalendar();
    const tabs = useMemo(() => [
        { name: 'overall' },
        { name: 'past7Days' },
        { name: 'select', callback: () => calendar.setFocusedInput(START_DATE), icon: 'caret-down' },
    ], [calendar]);
    const [currentRange, setCurrentRange] = useState({
        startRange: calendar.since,
        endRange: calendar.till
    });

    const avatarList = useSelector(getAccountAvatars);
    const memberList = useSelector(getCurrentClubApprovedMembers);
    const member = memberList.find(member => member._id.toString() === memberId);

    const [mode, setMode] = useState('overall');
    const [dateFrom, dateTo] = useMemo(() => {
      if (mode === 'overall') return [null, null];
      if (mode === 'past7Days') return [
        moment().subtract(7, 'days').toISOString(),
        moment().toISOString(),
      ];
      return [
        moment(calendar.since).toISOString(),
        moment(calendar.till).toISOString(),
      ];
    }, [calendar, mode]);

    const roleInClub = useSelector(getRoleInCurrentClub);

    useEffect(() => {
      dispatch(memberDetailsRequest(memberId, dateFrom, dateTo));
    }, [dateFrom, dateTo]);

    const deleteMember = () => {
        dispatch(openDialog('deleteMember', { 
            memberId,
            onConfirm: onConfirmDeleteMember,
            clubId,
         }));
    };

    const editMemberInfo = () => {
        dispatch(openDialog('editMemberInfo', { memberId }));
    };

    const onConfirmDeleteMember = useCallback( () => {
        history.push(`/club/${clubId}/members`);
    },[dispatch, clubId]);

    const requestChangeRole = useCallback(({ memberId, newRole }) => {
        dispatch(changeMemberRoleRequest(memberId, parameters.accountTypes[newRole]));
    }, [dispatch]);

    const changeRole = useCallback(e => {
        const newRole = e.target.value;
        dispatch(openDialog(
            'changeRole',
            {
                newRole,
                prevRole: parameters.accountTypeIds[member.typeId],
                memberId: member._id,
                username: member.username,
                realDialog: true,
                footer: 'cancelOrConfirm',
                onConfirm: requestChangeRole,
            },
        ));
    }, [member, clubId, requestChangeRole]);

    const openAgentDetails = () => {
        dispatch(openDialog('agentDetails', {memberId: member.roleAccountId, clubId: clubId}));
    }

    return (
        <div className="memberDetails clubPage">
            <Header title="memberDetails" to={`/club/${clubId}/members`}/>
            <div className="headerIcons">
                <Link to={`/club/${clubId}/members/${memberId}/help`} className={`tutorialIcon`} />
                {
                    parameters.accountTypeIds[member.typeId] === 'agent' &&
                    <div className={`${parameters.accountTypeIds[member.typeId]}IconContainer`}>
                        <button className={`${parameters.accountTypeIds[member.typeId]}Icon`} onClick={()=> openAgentDetails()}></button>
                    </div>
                }
            </div>
            <div className="avatarContainer">
                <Avatar data={avatarList[memberId]} isIncrementedFrame />
                {/* <span className="editPencil" onClick={editMemberInfo}></span> */}
            </div>
            <div className="playerInfo">
                <span className="name">{member.username}</span>
                <p>
                    <span className="nickname"> {t('nickname')}: </span>
                    <span> {member.nickname}</span>
                </p>
                <p>
                    <span className="id">{t('id')}: {member.globalAccountId}</span>
                    <span> / {member._id}</span>
                </p>
                {/* <span className="lastLogin">{t.lastLogin}: 14:29 18/03/20</span> */}
            </div>
            <div className="subheader subheader--transparent">
                <Calendar.Body {...calendar} currentRange={currentRange} hideInputs />
                <RealTabSwitcher tabs={tabs} onChange={setMode} selected={mode} />
            </div>

            <MemberStats />

            {/* <OptionRow option={'trackPlayer'} section={'memberDetails'} to={'#'}/> */}
            <div className={`${member.isOwner || roleInClub === "agent" ? 'owner' : `${parameters.accountTypeIds[member.typeId]}`}`}>
                <div className={`roleSelection`}>{t('memberDetails.roleSelection')}</div>
                {roles.map((role) => (role === 'agent' ? configs.userExtraRoles.includes('agent') : true) &&
                    (
                        <React.Fragment key={role}>
                        { role === 'agent' && role === parameters.accountTypeIds[member.typeId] ?
                            <Link to={`/club/${clubId}/members/${member._id}/agentMenu`} className="radio">
                                <input type="radio"
                                    id={role}
                                    value={role}
                                    checked="checked"
                                    onChange={changeRole}
                                />
                                <label htmlFor={role}>{t(role)}</label>
                                {role === 'agent' ? (<span className="arrowIcon agent__arrow-icon">
                                    <FontAwesomeIcon icon={faChevronRight} className="fa chevron-left" />
                                </span>) : false}
                            </Link>
                            :
                            <div  className="radio" >
                                <input type="radio"
                                    id={role}
                                    value={role}
                                    checked={role === parameters.accountTypeIds[member.typeId]}
                                    onChange={changeRole}
                                />
                                <label htmlFor={role}>{t(role)}</label>
                            </div>
                        }
                    </React.Fragment>
                    )
                )}
                <div className="delete">
                    <span className="deleteIcon" onClick={()=>deleteMember(member._id)}>{t('deleteMember')}</span>
                </div>
            </div>
        </div>
    );
};

export default MemberDetails;
